import React, { useMemo, useState } from 'react'
import { PlusOutlined } from '@ant-design/icons'
import { useForm } from 'antd/es/form/Form'
import { Button, Form, Modal, Select } from 'antd'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import _ from 'lodash'
import { currentUserSortedCompaniesSelector } from '../../../redux/session/currentUser/selectors'
import { useContextParameters } from '../../../services/api/hooks/useContextParameters'
import { useCtxSubsidiaries as useSubsidiaries } from '../queries/useSubsidiaries'
import { useAddSubsidiary } from '../queries/useAddSubsidiary'

type FormValues = { ids: string[] }

interface SubsidiariesFormProps {
  groupId?: string
}

const SubsidiariesForm: React.FC<SubsidiariesFormProps> = ({ groupId }) => {
  const { t } = useTranslation()
  const [form] = useForm<FormValues>()
  const [open, setOpen] = useState(false)

  const { companyId } = useContextParameters()
  const companies = useSelector(currentUserSortedCompaniesSelector)
  const { data: subsidiaries = [] } = useSubsidiaries()
  const { mutateAsync: addSubsidiary, isPending } = useAddSubsidiary()

  const handleOpen = () => setOpen(true)

  const handleCancel = () => {
    form.resetFields()
    setOpen(false)
  }

  const handleOk = async () => {
    try {
      const { ids } = await form.validateFields()
      const subsidiariesData = ids.map(id => ({
        id,
        name: _.keyBy(companies, 'id')[id]?.name
      }))
      await addSubsidiary(subsidiariesData)
      form.resetFields()
      setOpen(false)
    } catch {
      // validation failed, keep modal open
    }
  }

  const selectOptions = useMemo(
    () =>
      companies.map(c => ({
        label: c.name,
        key: c.id,
        value: c.id,
        disabled: [...subsidiaries.map(s => s.id), groupId, companyId].includes(c.id)
      })),
    [companies, subsidiaries, groupId, companyId]
  )

  return (
    <>
      <Button data-cy="subsidiaries-add-btn" type="primary" icon={<PlusOutlined />} onClick={handleOpen}>
        {t('global:add-subsidiaries')}
      </Button>
      <Modal
        open={open}
        title={t('global:add-subsidiaries')}
        okText={t('global:add')}
        cancelText={t('global:cancel')}
        okButtonProps={{ loading: isPending }}
        onOk={handleOk}
        onCancel={handleCancel}
        destroyOnHidden
        maskClosable={false}
        data-cy="subsidiaries-add-modal"
      >
        <Form form={form} layout="vertical">
          <Form.Item name="ids" rules={[{ required: true, message: t('global:required-field') }]}>
            <Select
              data-cy="subsidiaries-add-select"
              mode="multiple"
              style={{ width: '100%' }}
              filterOption={(input, option) => {
                const companyName = (option?.label as string)?.toLowerCase()
                return (companyName?.indexOf(input.toLowerCase()) ?? -1) >= 0
              }}
              options={selectOptions}
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  )
}

export default SubsidiariesForm
